// Pure caching policy for the engineer's site and lift history (Session AEK, Director decision D33;
// scoped by Session AGS and again by Session ASB, D-SEC-6, to the sites where the engineer holds
// OPEN work). No IndexedDB, no fetch, no DOM: the store is injected (the idb adapter implements it) and
// so is the fetch, exactly as sync.ts / messageSync.ts do it, so the decisions that matter (is
// this fresh, may we serve it offline, what happens when the network fails) are unit-testable.
//
// This is NOT an outbox. The three outboxes hold the engineer's own work and must never lose a
// record. This holds read-only reference data pulled from the server, so it is disposable: stale
// entries are simply re-fetched, and a wipe costs nothing but a round trip.
//
// The engineer diary's offline behaviour is a SERVICE WORKER stale-while-revalidate route (the
// Session AH exception, locked). That mechanism is deliberately not extended here: adding URLs to
// it would mean editing the service worker, which this session must not do, and it offers no way
// to express the two different lifetimes below. The IndexedDB store pattern used by every other
// cached thing in this app is the one that is followed.
import { RAN_OUT_END_REASONS, sessionEndReasonOf } from "../sessionEvents";

export interface CachedHistoryEntry {
  key: string;
  // Whatever the endpoint returned. Typed at the call site; the cache itself is agnostic.
  data: unknown;
  cachedAt: string; // ISO
}

export interface HistoryCacheStore {
  getEntry(key: string): Promise<CachedHistoryEntry | undefined>;
  putEntry(entry: CachedHistoryEntry): Promise<void>;
  // Session ASB: drop ONE entry, for a read the server now answers 404 (see readThroughCache).
  deleteEntry(key: string): Promise<void>;
  clear(): Promise<void>;
}

// Session ASB (D-SEC-6): the history store can also be walked, so a successful site-list sync can drop
// every cached page that belongs to a site no longer on the list (pruneHistoryCache, below).
export interface PrunableHistoryCacheStore extends HistoryCacheStore {
  listEntries(): Promise<CachedHistoryEntry[]>;
}

// The site list changes rarely (a new site is an event, not a daily occurrence), so a day is
// generous and keeps the browse screen instant. A site's or a lift's history can gain a visit at
// any time, so an hour keeps it honest without re-fetching on every tap.
export const SITE_LIST_TTL_MS = 24 * 60 * 60 * 1000;
export const HISTORY_TTL_MS = 60 * 60 * 1000;

// One completed job sheet (Session AEM). A day, like the site list, and for a similar reason:
// the record is finished. It is NOT strictly immutable -- the office can amend a submitted sheet
// (Session ABD) and can revert a backdated entry (Session AEB) -- but both are rare, deliberate
// office actions on a historical record, not the steady drip of new visits that keeps the hour on
// the history feeds honest. A day old is the right trade for a record an engineer wants to read
// in a basement, and a stale read is labelled either way.
export const JOB_SHEET_TTL_MS = 24 * 60 * 60 * 1000;

// VERSIONED (Session AEM). The cached payload's SHAPE changed when the site row gained
// `archived`, and a cache entry written under the old shape has no such key, so every site would
// file as current and the archived section would not appear at all until the entry aged out. The
// degradation is harmless (it is exactly the previous behaviour) and bounded by the 24h lifetime,
// but a one-character key change costs a single round trip and removes the window entirely.
// Bump the suffix whenever the site row's shape changes again.
//
// v3 (Session AGS) bumps it for a reason the original note did not anticipate: the shape did not
// change, its MEANING did. This list used to be every site on the account and is now only the sites
// the engineer has work at. A fresh entry is served without touching the network, so without the
// bump every phone that cached the list before the deploy would keep showing the whole customer
// base for up to 24 hours -- the exposure still open on exactly the devices the change is for. One
// round trip closes the window outright.
//
// v4 (Session ASB, Director decision D-SEC-6, 25 September 2026) bumps it for the same reason again:
// the list's meaning narrowed from "every site I have EVER had work at" to "the sites where I hold
// OPEN work now". The first fetch under v4 is also a successful sync, so it prunes every cached page
// for a site that has left the list (pruneHistoryCache), and the old v3 entry with it.
export function siteListCacheKey(): string {
  return "engineer:sites:v4";
}

export function siteHistoryCacheKey(siteId: string, page: number): string {
  return `engineer:site:${siteId}:${page}`;
}

export function liftHistoryCacheKey(assetId: string, page: number): string {
  return `engineer:asset:${assetId}:${page}`;
}

// Keyed on the JOB id, which is what the endpoint and the history rows both use. The store holds
// arbitrary keys against an untyped envelope, so this needs no new object store and no IndexedDB
// version bump: it is another key namespace in the one this session already has.
export function jobSheetCacheKey(jobId: string): string {
  return `engineer:jobsheet:${jobId}`;
}

// One page of the engineer's OWN completed visits, the "My past visits" list (Session ASB fix review).
// Saved so the list, and through it the own records the prune keeps, can be reached with no signal. It is
// the engineer's own data, which D-SEC-6 lets them keep: the prune leaves it (historyEntryOwner, below)
// and sign-out's clear wipes it with the rest of the history.
export function myVisitsCacheKey(page: number): string {
  return `engineer:myvisits:${page}`;
}

// Fresh means "within its lifetime". A missing or unparseable timestamp is treated as stale
// rather than fresh: the failure mode of re-fetching is a round trip, and the failure mode of
// trusting a bad timestamp is showing an engineer service history that is silently out of date.
export function isFresh(entry: CachedHistoryEntry | undefined, ttlMs: number, now: Date = new Date()): boolean {
  if (!entry) return false;
  const cachedAt = Date.parse(entry.cachedAt);
  if (Number.isNaN(cachedAt)) return false;
  const age = now.getTime() - cachedAt;
  // A timestamp from the future means the device clock moved; treat it as stale, never as
  // indefinitely fresh.
  if (age < 0) return false;
  return age < ttlMs;
}

// What a read produced, and where it came from. The caller renders "showing saved information"
// only when `stale` is true, so an engineer is never quietly told that day-old history is live.
export interface CacheReadResult<T> {
  data: T;
  fromCache: boolean;
  // True when the network could not be reached and a cache entry past its lifetime was served
  // anyway. Better than an empty screen underground, but the engineer is told.
  stale: boolean;
}

// Read through the cache: serve a fresh entry without touching the network, otherwise fetch and
// store. If the fetch fails and ANY cached entry exists (however old) it is served with
// `stale: true`, because a basement with no signal is the normal case for this app and stale
// history beats a blank screen. With no cache and no network, the error propagates.
// A failure that must NEVER be papered over with cached data. The stale fallback exists for "no
// signal", and it is only honest if it can tell that apart from "the server refused you". An
// account deactivated by the office (the platform's hard-revoke marker) answers 401, and serving
// the cache then would show a revoked engineer the whole company site list under a notice saying
// they are offline, which is both false and a revocation bypass. Every 401 and 403 is refused this
// way; whether the saved copies are also EMPTIED is a separate question, isRevocation's.
export function isAuthFailure(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return status === 401 || status === 403;
}

// Which refusals TAKE ACCESS AWAY, and so empty the whole store (Session ASB, the review of fix round 2; one rule with
// the auth layer since fix round 4). A 403 is the server refusing this account the data outright. A 401 is a session
// that has ended, and it takes access away UNLESS the session merely RAN OUT: its reason is one of RAN_OUT_END_REASONS
// (sessionEvents.ts: idle_timeout, session_expired, or no_session, which is also what the reasonless
// { error: "unauthorized" } reads as). That is the SAME list the auth layer keeps an engineer's offline copies by
// (auth.tsx keepsOfflineCopies), read from the body by the SAME function (sessionEndReasonOf), so the two layers cannot
// disagree: signed_out (signed out elsewhere, "sign out everywhere", a deactivation, a password reset, a deleted
// account), an ended auditor window, and any reason a newer server adds all wipe here, as they do there. A session that
// ran out takes nothing away here: the auth layer keeps an engineer's copies for it, so they can still read their own
// records and My past visits in a basement once they sign in again, and it is the auth layer, not this cache, that
// decides whether they go. A 401 whose body is not the guard's answer at all says nothing about the session, and is
// left to the auth layer the same way. (api.ts puts the parsed body on ApiError.body; the prose on ApiError.code is
// never what decides.)
export function isRevocation(err: unknown): boolean {
  const failure = err as { status?: unknown; body?: unknown } | null;
  if (failure?.status === 403) return true;
  if (failure?.status !== 401) return false;
  const reason = sessionEndReasonOf(failure.body);
  return reason !== null && !RAN_OUT_END_REASONS.has(reason);
}

// Session ASB (D-SEC-6): the server answers 404 for a site, lift or record the engineer may no longer
// read (the same answer as one that does not exist, by design). That is not "no signal" either: the
// server has said this is not theirs to read any more, so the saved copy must not be served as
// "saved information" for ever. Only that one entry goes; the rest of the cache is still theirs.
export function isGone(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return status === 404 || status === 410;
}

export async function readThroughCache<T>(
  store: HistoryCacheStore,
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>,
  now: () => Date = () => new Date(),
): Promise<CacheReadResult<T>> {
  let entry: CachedHistoryEntry | undefined;
  try {
    entry = await store.getEntry(key);
  } catch {
    entry = undefined; // a broken cache must never block a fetch
  }

  if (isFresh(entry, ttlMs, now())) {
    return { data: entry!.data as T, fromCache: true, stale: false };
  }

  try {
    const data = await fetcher();
    try {
      await store.putEntry({ key, data, cachedAt: now().toISOString() });
    } catch {
      // Cache write failed (quota, private mode). The data is still good; serve it.
    }
    return { data, fromCache: false, stale: false };
  } catch (err) {
    if (isAuthFailure(err)) {
      // Refused, not unreachable: the caller fails and nothing is served from the cache. When the refusal
      // takes access away (isRevocation) the cache is emptied too, since this data is only theirs to read
      // while the server says so; a session that merely ran out leaves it for the auth layer to decide.
      if (isRevocation(err)) {
        try {
          await store.clear();
        } catch {
          // Nothing more to do; the throw below still denies the read.
        }
      }
      throw err;
    }
    if (isGone(err)) {
      // Session ASB: out of scope (or deleted). Drop THIS entry and fail, never serve it stale.
      try {
        await store.deleteEntry(key);
      } catch {
        // Nothing more to do; the throw below still denies the read.
      }
      throw err;
    }
    if (entry) return { data: entry.data as T, fromCache: true, stale: true };
    throw err;
  }
}

// ---- Pruning on a successful site-list sync (Session ASB, Director decision D-SEC-6) ----------
//
// The site list is the server's statement of which sites the engineer may read NOW (open work there).
// Everything cached for a site that is no longer on it has to leave the device, or the phone would go
// on showing a finished site's history, lifts and colleagues' visits as "saved information" until the
// engineer signs out, which is exactly what D-SEC-6 takes away.
//
// Which site each entry belongs to:
//   - a site page (engineer:site:<siteId>:<page>) names its site in the key;
//   - a lift page (engineer:asset:<assetId>:<page>) carries its lift's site as data.asset.siteId
//     (the server sends it since ASB; an entry cached before that carries none and is dropped, which
//     costs one round trip);
//   - a job sheet (engineer:jobsheet:<jobId>) carries data.siteId. A lift-less record (siteId null)
//     is only ever readable when it is the engineer's OWN job, which they keep for ever, so it stays.
//     A job sheet at a site that has left scope goes, EXCEPT the engineer's own: the server marks that
//     one `own: true` (since the ASB review), and "My past visits" still opens it;
//   - a "My past visits" page (engineer:myvisits:<page>) stays whatever sites it names: it is the
//     engineer's own list, and the way back to those own records with no signal (the ASB fix review);
//   - the CURRENT site-list entry stays; an older site-list version (v2, v3) is dropped;
//   - anything else is not a key this module writes, so it is dropped too (a disposable cache).
export type HistoryEntryOwner = { kind: "keep" } | { kind: "drop" } | { kind: "site"; siteId: string | null | undefined };

export function historyEntryOwner(entry: CachedHistoryEntry): HistoryEntryOwner {
  const key = entry.key;
  if (key === siteListCacheKey()) return { kind: "keep" };
  if (key.startsWith("engineer:sites:")) return { kind: "drop" };
  if (/^engineer:myvisits:\d+$/.test(key)) return { kind: "keep" };
  const site = /^engineer:site:(.+):\d+$/.exec(key);
  if (site) return { kind: "site", siteId: site[1] };
  if (/^engineer:asset:.+:\d+$/.test(key)) {
    const siteId = (entry.data as { asset?: { siteId?: unknown } } | null)?.asset?.siteId;
    return { kind: "site", siteId: typeof siteId === "string" ? siteId : undefined };
  }
  if (key.startsWith("engineer:jobsheet:")) {
    // The engineer's OWN visit stays whatever its site (Session ASB review, I6-1): the server marks it
    // `own: true`, D-SEC-6 lets them keep their own past jobs, and "My past visits" opens it.
    if ((entry.data as { own?: unknown } | null)?.own === true) return { kind: "keep" };
    const siteId = (entry.data as { siteId?: unknown } | null)?.siteId;
    if (siteId === null) return { kind: "keep" }; // a lift-less record: the engineer's own job
    return { kind: "site", siteId: typeof siteId === "string" ? siteId : undefined };
  }
  return { kind: "drop" };
}

// Pure: the keys a sync with this site list must drop.
export function historyKeysOutOfScope(entries: CachedHistoryEntry[], inScopeSiteIds: ReadonlySet<string>): string[] {
  const drop: string[] = [];
  for (const entry of entries) {
    const owner = historyEntryOwner(entry);
    if (owner.kind === "keep") continue;
    if (owner.kind === "site" && typeof owner.siteId === "string" && inScopeSiteIds.has(owner.siteId)) continue;
    drop.push(entry.key);
  }
  return drop;
}

// Drop every cached entry for a site that is not on `inScopeSiteIds`. Resolves to how many went.
export async function pruneHistoryCache(store: PrunableHistoryCacheStore, inScopeSiteIds: ReadonlySet<string>): Promise<number> {
  const keys = historyKeysOutOfScope(await store.listEntries(), inScopeSiteIds);
  for (const key of keys) await store.deleteEntry(key);
  return keys.length;
}
